import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  Divider,
  Grid,
  Paper,
  Tabs,
  Tab
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../../components/Header";
import "./SupplyRegistration.css";

// 分割したコンポーネント
import SupplyDetails from "./registration/SupplyDetails";
import RemainingUpdateSection from "./registration/RemainingUpdateSection";
import ConfirmDialog from "./registration/ConfirmDialog";
import ClientAutocomplete from "../../components/ClientAutocomplete";
import ProductSelector from "./registration/ProductSelector"; // 🚀 修正：registration/ProductSelector

// Boolean型安全変換ユーティリティ
import { ensureBooleanForAPI } from "../../utils/booleanUtils";

// MUIアイコン
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined';
import WaterDropIcon from '@mui/icons-material/WaterDrop';

const SupplyRegistration = () => {
  // タブ管理
  const [activeTab, setActiveTab] = useState(0);
  
  // 基本データの状態
  const [products, setProducts] = useState([]);
  
  // 共通で使用する顧客・製品選択の状態
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedClientProductId, setSelectedClientProductId] = useState("");
  const [selectedClientProduct, setSelectedClientProduct] = useState(null);
  
  // 供給登録関連の状態
  const [supplyAmount, setSupplyAmount] = useState("");
  const [supplyAmountError, setSupplyAmountError] = useState("");
  const [supplyDate, setSupplyDate] = useState(new Date().toISOString().split("T")[0]);
  const [isExceptional, setIsExceptional] = useState(false);
  
  // 満タンチェック関連
  const [isTankFull, setIsTankFull] = useState(true);
  const [manualRemainingAmount, setManualRemainingAmount] = useState("");
  const [manualRemainingError, setManualRemainingError] = useState("");
  
  // 新機能: 残量更新スキップオプション（デフォルトはfalse = 更新する）
  const [skipRemainingUpdate, setSkipRemainingUpdate] = useState(false);
  
  // UI状態
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 確認ダイアログの状態
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [supplyToRegister, setSupplyToRegister] = useState(null);
  
  const navigate = useNavigate();

  // タブ切り替え処理
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // 製品データの安全性チェック関数
  const filterActiveProducts = useCallback((productsArray) => {
    if (!Array.isArray(productsArray)) return [];
    
    return productsArray.filter(product => 
      product && 
      product.id && 
      product.name && 
      product.active !== false
    );
  }, []);

  // 製品一覧取得
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = sessionStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        
        const productResponse = await axios.get("/api/products", { headers });
        
        const rawProducts = productResponse.data || [];
        const safeProducts = filterActiveProducts(rawProducts);
        
        if (rawProducts.length > 0 && safeProducts.length === 0) {
          setError('利用可能な製品がありません。製品を登録してください。');
        } else if (safeProducts.length === 0) {
          // 製品が登録されていない場合は特にエラーとしない
        }
        
        setProducts(safeProducts);
        setError(null);
      } catch (err) {
        setError(err.message);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [filterActiveProducts]);

  // 顧客が選択されたときの処理
  useEffect(() => {
    if (selectedClient) {
      // 製品選択をリセット
      setSelectedProductId("");
      setSelectedClientProductId("");
      setSelectedClientProduct(null);
    } else {
      setSelectedProductId("");
      setSelectedClientProductId("");
      setSelectedClientProduct(null);
    }
  }, [selectedClient]);

  // 製品選択時の処理（ProductSelectorから呼ばれる）
  const handleProductChange = (productId) => {
    setSelectedProductId(productId);
  };

  // 顧客製品ID設定時の処理（ProductSelectorから呼ばれる）
  const handleClientProductChange = async (clientProductId) => {
    setSelectedClientProductId(clientProductId);
    
    if (clientProductId) {
      // 顧客製品の詳細情報を取得
      try {
        const token = sessionStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        
        const response = await axios.get(`/api/client-products/${clientProductId}`, { headers });
        setSelectedClientProduct(response.data);
      } catch (err) {
        setSelectedClientProduct(null);
      }
    } else {
      setSelectedClientProduct(null);
    }
  };

  // 供給量変更時の処理
  const handleSupplyAmountChange = (value) => {
    if (value === "" || /^\d+$/.test(value)) {
      setSupplyAmount(value);
      
      const num = parseInt(value, 10);
      if (value === "" || isNaN(num) || num < 1 || num > 200000) {
        setSupplyAmountError("1〜200000の範囲で入力してください");
      } else {
        setSupplyAmountError("");
      }
    }
  };
  
  // 手動残量入力時の処理
  const handleManualRemainingAmountChange = (value) => {
    if (value === "" || /^\d+$/.test(value)) {
      setManualRemainingAmount(value);
      
      const num = parseInt(value, 10);
      if (value === "" || isNaN(num) || num < 0) {
        setManualRemainingError("0以上の値を入力してください");
      } else if (selectedClientProduct?.totalCapacity && num > selectedClientProduct.totalCapacity) {
        setManualRemainingError(`タンク容量（${selectedClientProduct.totalCapacity}L）以下で入力してください`);
      } else {
        setManualRemainingError("");
      }
    }
  };
  
  // 確認メッセージ生成
  const generateConfirmMessage = () => {
    const clientName = selectedClient?.name || "";
    
    const selectedProduct = products.find(p => p.id === parseInt(selectedProductId, 10));
    const productName = selectedProduct?.name || "不明な製品";
    
    let confirmText = `以下の内容で登録しますか？\n` +
        `顧客: ${clientName}\n` +
        `製品: ${productName}\n` +
        `供給量: ${supplyAmount}L\n` +
        `供給日: ${supplyDate}`;
        
    // 残量更新情報を追加
    if (skipRemainingUpdate) {
      confirmText += `\n\n⚠️ 残量情報は更新されません`;
    } else {
      if (selectedClientProduct?.totalCapacity) {
        if (isTankFull) {
          confirmText += `\n供給後: タンクは満タン（${selectedClientProduct.totalCapacity}L）になります`;
        } else {
          confirmText += `\n供給後の残量: ${manualRemainingAmount}L`;
        }
      } else {
        confirmText += `\n残量情報: 更新されます`;
      }
    }
    
    return confirmText;
  };
  
  // フォーム送信(登録ボタン押下)時
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedClientProductId || !supplyAmount || !supplyDate) {
      alert("すべての項目を入力してください。");
      return;
    }

    const amount = parseInt(supplyAmount, 10);
    if (isNaN(amount) || amount < 1 || amount > 200000) {
      alert("供給量は1〜200000の範囲で入力してください。");
      return;
    }
    
    if (!selectedClientProduct) {
      alert("製品を選択してください。");
      return;
    }
    
    const isValidProduct = products.find(p => p.id === parseInt(selectedProductId, 10));
    if (!isValidProduct) {
      alert("選択された製品が無効です。ページを再読み込みしてください。");
      return;
    }
    
    // 残量更新をスキップしない場合のみバリデーション実行
    if (!skipRemainingUpdate && selectedClientProduct?.totalCapacity && !isTankFull) {
      if (!manualRemainingAmount) {
        alert("タンクが満タンでない場合は、残量を入力してください。");
        return;
      }
      
      const remaining = parseInt(manualRemainingAmount, 10);
      if (remaining > selectedClientProduct.totalCapacity) {
        alert(`残量はタンク容量（${selectedClientProduct.totalCapacity}L）以下で入力してください。`);
        return;
      }
    }
    
    const token = sessionStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      // 履歴数とプラン上限のチェック
      const countRes = await axios.get(`/api/supplies/count?clientProductId=${selectedClientProductId}`, { headers });
      const limitRes = await axios.get("/api/supplies/user/plan", { headers });
      const currentCount = countRes.data.count;
      const maxHistory = limitRes.data.maxHistory;

      // 🚀 修正：Boolean値の安全な変換
      const supply = {
        clientProductId: selectedClientProductId,
        amount: parseInt(supplyAmount, 10),
        supplyDate: supplyDate,
        isExceptional: ensureBooleanForAPI(isExceptional)
      };

      // 残量更新をスキップしない場合のみ、残量関連の情報を含める
      if (!skipRemainingUpdate) {
        supply.isFull = isTankFull;
        
        if (!isTankFull) {
          supply.remainingAmount = parseInt(manualRemainingAmount, 10);
        }
      }

      if (currentCount >= maxHistory) {
        // 古い履歴を削除するメッセージ
        const oldestRes = await axios.get(`/api/supplies/oldest?clientProductId=${selectedClientProductId}`, { headers });
        const oldest = oldestRes.data;
        setConfirmMessage(
          oldest?.supplyDate
            ? `履歴が上限に達しているため、${new Date(oldest.supplyDate).toLocaleDateString()} の履歴が削除されます。よろしいですか？`
            : "履歴が上限に達しているため、最も古い履歴が削除されます。よろしいですか？"
        );
      } else {
        setConfirmMessage(generateConfirmMessage());
      }

      setSupplyToRegister(supply);
      setConfirmOpen(true);
    } catch (err) {
      alert("登録前のチェック中にエラーが発生しました。");
    }
  };

  // 確認モーダル(OKボタン)押下時
  const confirmRegister = async () => {
    if (!supplyToRegister) {
      alert("エラー: 登録データが存在しません");
      return;
    }

    const token = sessionStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      // 古い履歴を削除
      if (confirmMessage.includes("削除")) {
        const oldestRes = await axios.get(`/api/supplies/oldest?clientProductId=${selectedClientProductId}`, { headers });
        await axios.delete(`/api/supplies/${oldestRes.data.id}`, { headers });
      }

      // 新規登録
      const response = await axios.post("/api/supplies", supplyToRegister, { headers });

      setConfirmOpen(false);
      alert("登録が完了しました。");
      
      // 入力状態を完全にリセット
      resetSelections();
      
    } catch (err) {
      alert("登録に失敗しました。");
    }
  };
  
  // フォームリセット（供給詳細のみ）
  const resetForm = () => {
    setSupplyAmount("");
    setSupplyDate(new Date().toISOString().split("T")[0]);
    setIsExceptional(false);
    setIsTankFull(true);
    setManualRemainingAmount("");
    setManualRemainingError("");
    setSupplyAmountError("");
    setSkipRemainingUpdate(false);
  };
  
  // 完全リセット（顧客・製品選択も含む）
  const resetSelections = () => {
    setSelectedClient(null);
    setSelectedProductId("");
    setSelectedClientProductId("");
    setSelectedClientProduct(null);
    
    resetForm();
    
    setError(null);
  };
  
  return (
    <>
      <Header />
      <Box sx={{ 
        maxWidth: '800px',
        mx: 'auto', 
        px: { xs: 2, sm: 4 },
        py: 3,
        pb: { xs: 12, sm: 6 },
        minHeight: { xs: 'calc(100vh - 200px)', sm: 'auto' }
      }}>
        {/* タブ切り替え */}
        <Card sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            indicatorColor="primary"
            textColor="primary"
            aria-label="operation type tabs"
            sx={{
              '& .MuiTab-root': {
                minHeight: { xs: 64, sm: 48 },
                fontSize: { xs: '0.9rem', sm: '1rem' }
              }
            }}
          >
            <Tab 
              icon={<LocalGasStationOutlinedIcon />} 
              label="供給登録" 
              id="tab-0" 
              aria-controls="tabpanel-0"
            />
            <Tab 
              icon={<WaterDropIcon />} 
              label="残量更新" 
              id="tab-1" 
              aria-controls="tabpanel-1"
            />
          </Tabs>
        </Card>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              bgcolor: 'error.light', 
              color: 'error.dark',
              borderRadius: 1,
              textAlign: 'center',
              mb: 3
            }}
          >
            <Typography>エラー: {error}</Typography>
          </Paper>
        ) : (
          <>
            {/* 供給登録タブ */}
            {activeTab === 0 && (
              <Box display={activeTab === 0 ? 'block' : 'none'} role="tabpanel" id="tabpanel-0" aria-labelledby="tab-0">
                <Card elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white',
                    py: 2, 
                    textAlign: 'center'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      供給登録
                    </Typography>
                  </Box>
                  
                  <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
                    <Box component="form" onSubmit={handleSubmit}>
                      <Grid container spacing={3}>
                        {/* 左カラム: 顧客・製品情報 */}
                        <Grid item xs={12} md={6}>
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              mb: 2, 
                              fontWeight: 500,
                              color: 'primary.main',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            顧客・製品の選択
                          </Typography>
                          
                          <ClientAutocomplete
                            selectedClient={selectedClient}
                            onClientChange={setSelectedClient}
                            label="顧客"
                            placeholder="顧客名で検索..."
                            required
                            sx={{ 
                              mb: 3,
                              '& .MuiInputBase-root': {
                                minHeight: { xs: 56, sm: 48 }
                              }
                            }}
                          />
                          
                          <ProductSelector
                            clientId={selectedClient?.id}
                            products={products}
                            selectedProductId={selectedProductId}
                            onProductChange={handleProductChange}
                            onClientProductChange={handleClientProductChange}
                          />
                        </Grid>
                        
                        {/* 右カラム: 供給詳細 */}
                        <Grid item xs={12} md={6}>
                          <SupplyDetails
                            supplyAmount={supplyAmount}
                            supplyAmountError={supplyAmountError}
                            supplyDate={supplyDate}
                            isExceptional={isExceptional}
                            isTankFull={isTankFull}
                            manualRemainingAmount={manualRemainingAmount}
                            manualRemainingError={manualRemainingError}
                            selectedClientProduct={selectedClientProduct}
                            skipRemainingUpdate={skipRemainingUpdate}
                            onSkipRemainingUpdateChange={setSkipRemainingUpdate}
                            onSupplyAmountChange={handleSupplyAmountChange}
                            onSupplyDateChange={setSupplyDate}
                            onExceptionalChange={setIsExceptional}
                            onTankFullChange={setIsTankFull}
                            onManualRemainingAmountChange={handleManualRemainingAmountChange}
                          />
                        </Grid>
                      </Grid>
                      
                      <Divider sx={{ my: 4 }} />
                      
                      {/* 登録・リセットボタン */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        gap: 2,
                        mb: 2
                      }}>
                        <Button 
                          type="submit" 
                          variant="contained"
                          size="large"
                          disabled={Boolean(supplyAmountError) || !selectedClientProductId}
                          startIcon={<SaveIcon />}
                          sx={{ 
                            minWidth: { xs: 140, sm: 120 },
                            minHeight: { xs: 48, sm: 40 },
                            px: 3
                          }}
                        >
                          登録
                        </Button>
                        <Button 
                          type="button" 
                          variant="outlined"
                          size="large"
                          onClick={resetSelections}
                          startIcon={<RestartAltIcon />}
                          sx={{ 
                            minWidth: { xs: 140, sm: 120 },
                            minHeight: { xs: 48, sm: 40 },
                            px: 3
                          }}
                        >
                          リセット
                        </Button>
                      </Box>
                      
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          display: 'block', 
                          textAlign: 'center',
                          color: 'text.secondary',
                          mt: 2
                        }}
                      >
                        すべての項目を入力し、登録ボタンを押してください
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            )}
            
            {/* 残量更新タブ */}
            {activeTab === 1 && (
              <Box display={activeTab === 1 ? 'block' : 'none'} role="tabpanel" id="tabpanel-1" aria-labelledby="tab-1">
                <RemainingUpdateSection
                  products={products}
                />
              </Box>
            )}
          </>
        )}
      </Box>
      
      {/* 確認ダイアログ */}
      <ConfirmDialog
        open={confirmOpen}
        message={confirmMessage}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmRegister}
      />
    </>
  );
};

export default SupplyRegistration;